<script setup>
import { computed } from 'vue'
import { useStripeConnect } from '@/composables/useStripeConnect'
import FormButton from '@/components/Atoms/FormButton.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'

// Stripe Connect status of the owner's paywall account as seen from a site:
// connected / pending / not connected (+ live/test mode, masked account id),
// with Connect and Unlink actions. Reads castr GET /sites/:id/monetization.
const props = defineProps({
	// { connected, status, accountId, mode, requirements, checkedAt }
	stripe: {
		type: Object,
		default: null,
	},
	// { status: active|paused, at, pausedProducts }
	monetization: {
		type: Object,
		default: null,
	},
	unlinking: {
		type: Boolean,
		default: false,
	},
	// hide the unlink action (e.g. Overview shows a link to Plans instead)
	showUnlink: {
		type: Boolean,
		default: true,
	},
})
const emit = defineEmits(['connected', 'unlink'])

const { connecting, failed, error, connect, reopen, cancel } = useStripeConnect({
	onConnected: () => emit('connected'),
})

const status = computed(() => props.stripe?.status || 'none')
const connected = computed(() => props.stripe?.connected === true)
const loaded = computed(() => Boolean(props.stripe))

const pill = computed(() => {
	if (!loaded.value) return { variant: 'muted', text: 'Checking…' }
	if (connected.value) return { variant: 'success', text: 'Connected' }
	if (status.value === 'pending') return { variant: 'warning', text: 'Onboarding incomplete' }
	if (status.value === 'disabled') return { variant: 'warning', text: 'Disabled by Stripe' }
	if (status.value === 'mismatch') return { variant: 'warning', text: 'Different account' }
	if (status.value === 'unknown') return { variant: 'muted', text: 'Unavailable' }
	return { variant: 'muted', text: 'Not connected' }
})

const modeLabel = computed(() => {
	if (!connected.value || !props.stripe?.mode) return null
	return props.stripe.mode === 'live' ? 'Live mode' : 'Test mode'
})

const detail = computed(() => {
	if (!loaded.value) return ''
	if (connected.value) return 'Viewers can buy your plans. Payouts go to this Stripe account.'
	if (status.value === 'pending') return 'Stripe still needs details before it can accept payments. Continue the setup to finish.'
	if (status.value === 'unlinked') return 'You unlinked Stripe. Free and registration-only content keeps working; paid plans are paused.'
	if (status.value === 'mismatch') return 'Your Paywall is connected to another Stripe account than the one this site bills through. Contact support to move the site.'
	if (status.value === 'unknown') return 'The Paywall service did not answer. Try again in a moment.'
	return 'Not needed for free or registration-only sites. Connect when you want to sell plans.'
})

const pausedSince = computed(() => {
	if (props.monetization?.status !== 'paused') return null
	return props.monetization.at ? new Date(props.monetization.at).toLocaleDateString() : null
})
</script>

<template>
	<div data-testid="stripe-status">
		<div class="flex flex-wrap items-center gap-2">
			<StatusPill pill size="sm" :variant="pill.variant">{{ pill.text }}</StatusPill>
			<StatusPill v-if="modeLabel" pill size="sm" :variant="stripe.mode === 'live' ? 'success' : 'warning'">{{ modeLabel }}</StatusPill>
			<span v-if="connected && stripe.accountId" class="font-mono text-tiny text-surface-8" :title="'Stripe account'">{{ stripe.accountId }}</span>
			<StatusPill v-if="pausedSince !== null" pill size="sm" variant="warning">Paid plans paused{{ pausedSince ? ` since ${pausedSince}` : '' }}</StatusPill>
		</div>
		<p class="mb-0 mt-2 text-tiny text-surface-7">{{ detail }}</p>
		<p v-if="error" class="mb-0 mt-1 text-tiny text-error">{{ error }}</p>
		<p v-else-if="failed" class="mb-0 mt-1 text-tiny text-error">Stripe did not complete the link. Try again.</p>

		<div class="mt-3 flex flex-wrap items-center gap-2">
			<template v-if="connected">
				<router-link :to="{ path: '/paywall/v2', query: { view: 'settings' } }" class="text-tiny text-surface-8 underline hover:text-white">Manage in Paywall</router-link>
				<FormButton v-if="showUnlink" size="sm" type="danger" class="ml-auto" :is-loading="unlinking" :disabled="unlinking" @click="emit('unlink')">Unlink Stripe</FormButton>
			</template>
			<template v-else-if="loaded && status !== 'mismatch' && status !== 'unknown'">
				<template v-if="connecting">
					<span class="text-tiny text-surface-8">Finish in the Stripe tab — we're waiting…</span>
					<FormButton size="sm" type="secondary" @click="reopen">Reopen Stripe</FormButton>
					<FormButton size="sm" type="link" @click="cancel">Cancel</FormButton>
				</template>
				<FormButton v-else size="sm" @click="connect">{{ status === 'pending' || status === 'disabled' ? 'Continue Stripe setup' : 'Connect Stripe' }}</FormButton>
			</template>
		</div>
	</div>
</template>
